import { Injectable } from '@angular/core';
import { marked, Renderer } from 'marked';
import type { Tokens } from 'marked';

@Injectable({
  providedIn: 'root'
})
export class MarkdownService {
  constructor() {
    const renderer = new Renderer();
    const defaultTable = renderer.table.bind(renderer);
    renderer.table = (token: Tokens.Table) => {
      return `<div class="nova-md-table-scroll">${defaultTable(token)}</div>`;
    };

    // Configure marked options
    marked.setOptions({
      breaks: true, // Convert line breaks to <br>
      gfm: true, // GitHub Flavored Markdown
      renderer,
    });
  }

  /**
   * Convert markdown text to HTML
   */
  parseMarkdown(markdown: string): string {
    if (!markdown || markdown.trim() === '') {
      return '';
    }

    try {
      return marked.parse(this.normalizeTables(markdown)) as string;
    } catch (error) {
      console.error('Error parsing markdown:', error);
      return markdown; // Return original text if parsing fails
    }
  }

  /**
   * Defensive fix for malformed LLM-generated tables where the GFM delimiter
   * row is glued to the following data row on a single line, e.g.
   *   "|---|---|---|| Kept ... |"  ->  "|---|---|---|\n| Kept ... |"
   * GFM requires the delimiter row on its own line; otherwise the whole block
   * fails to parse as a table and renders as raw pipe text.
   */
  private normalizeTables(markdown: string): string {
    return markdown.replace(/^(\|(?:\s*:?-+:?\s*\|)+)(?=\|)/gm, '$1\n');
  }

  /**
   * Check if text contains markdown formatting
   */
  hasMarkdown(text: string): boolean {
    if (!text) return false;

    // Check for common markdown patterns
    const markdownPatterns = [
      /\*\*.*?\*\*/, // Bold
      /\*.*?\*/, // Italic
      /`.*?`/, // Inline code
      /```[\s\S]*?```/, // Code blocks
      /^#{1,6}\s/m, // Headers
      /^\s*[-*+]\s/m, // Lists
      /^\s*\d+\.\s/m, // Numbered lists
      /\[.*?\]\(.*?\)/, // Links
      /\|[\s\S]*\|/m, // Tables - any line containing pipe characters
    ];

    return markdownPatterns.some(pattern => pattern.test(text));
  }
}
